import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

// A card that opens beside what was clicked (Google's event bubble). On a phone it is a sheet
// from the bottom. Escape or a press outside closes it and focus goes back where it was. A press
// outside only closes: it does not also start booking under the pointer.
// Radix menus, selects and popovers opened from inside count as inside (they portal to <body>).
const INSIDE = '[data-sc-float], [data-radix-popper-content-wrapper], [role="listbox"], [role="dialog"]';

export default function Floating({ rect, onClose, width = 400, label, children }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  // The latest onClose, so the listeners below are set up once per opening.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const phone = typeof window !== 'undefined' && window.innerWidth < 640;

  useLayoutEffect(() => {
    if (phone || !ref.current) return;
    const vw = window.innerWidth; const vh = window.innerHeight;
    const h = ref.current.offsetHeight;
    const r = rect || { left: vw / 2, right: vw / 2, top: vh / 3, bottom: vh / 3 };
    // Right of the target if it fits, else left of it, else centred over it.
    let left = r.right + 12;
    if (left + width > vw - 12) left = r.left - width - 12;
    if (left < 12) left = Math.max(12, Math.min(vw - width - 12, (r.left + r.right) / 2 - width / 2));
    const top = Math.max(12, Math.min(vh - h - 12, r.top));
    setPos((p) => (p && p.left === left && p.top === top ? p : { left, top }));
  }, [rect, width, phone, children]);

  useEffect(() => {
    const before = document.activeElement;
    const t = setTimeout(() => {
      const el = ref.current && (ref.current.querySelector('[data-autofocus]') || ref.current.querySelector('button, input, [tabindex="0"]'));
      if (el) el.focus();
    }, 20);
    const key = (e) => { if (e.key === 'Escape' && !e.defaultPrevented) { e.stopPropagation(); closeRef.current(); } };
    const press = (e) => {
      if (e.target.closest && e.target.closest(INSIDE)) return;
      e.stopPropagation(); e.preventDefault();
      closeRef.current();
    };
    document.addEventListener('keydown', key);
    window.addEventListener('pointerdown', press, true);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', key);
      window.removeEventListener('pointerdown', press, true);
      if (before && before.focus && document.contains(before)) before.focus();
    };
  }, []);

  return createPortal(
    <>
      {phone && <div className="fixed inset-0 z-[55] bg-slate-900/30" aria-hidden />}
      <div ref={ref} data-sc-float role="dialog" aria-label={label}
        className={clsx('fixed z-[56] bg-white shadow-modal ring-1 ring-slate-200/70 outline-none',
          phone ? 'inset-x-0 bottom-0 rounded-t-2xl max-h-[85vh] overflow-y-auto pb-[env(safe-area-inset-bottom)]' : 'rounded-2xl max-h-[calc(100vh-24px)] overflow-y-auto',
          !phone && !pos && 'opacity-0')}
        style={phone ? undefined : { width, left: pos ? pos.left : -9999, top: pos ? pos.top : 0 }}>
        {children}
      </div>
    </>,
    document.body,
  );
}
